import ElasticApmRumActions from '@/interfaces/Metrics/ElasticApmRumActions';
import { onMounted, ref, Ref } from 'vue';
import { Span, SpanOptions, Transaction, TransactionOptions } from '@elastic/apm-rum';
import useMetrics from '@/composables/v2/metrics/useMetrics';
import SessionEventScope from '@/models/Metrics/SessionEventScope';
import SessionEventType from '@/models/Metrics/SessionEventType';
import { v4 as uuidv4 } from 'uuid';
import { onBeforeRouteLeave } from 'vue-router';
import { createSessionEvent, SessionEvent } from '@/models/Metrics/SessionEvent';
import getSessionEventTypeFromEventType from '@/models/Metrics/EventTypeSessionEventTypeMap';

const currentTransaction = ref<Transaction | undefined>();
const currentSpan = ref<Span | undefined>();
const additionalSpans: { [id: string]: Span } = {};
const allSpanNamesUsedInCurrentTransaction: string[] = [];

const defaultStepName = 'STEP-1';

/**
 * This composable should be used on old tasks to keep the compatibility with the old APM implementation.
 * New tasks should not use this composable.
 */
export default function useElasticApmRumMetricsAdapter(): ElasticApmRumActions {
    const { dispatchSessionEvents } = useMetrics();

    const logApmEvent = (eventName: string, ...args: unknown[]): void => {
        if (process.env.VUE_APP_DEBUG !== 'true') {
            return;
        }

        console.log(eventName, ...args);
    };

    const startTransaction = (
        name: string,
        type: string,
        options?: TransactionOptions
    ): Ref<Transaction | undefined> => {
        logApmEvent('startTransaction', name, type, options);

        const transaction = {
            name,
            type,
            duration() {
                console.warn('Transaction.duration not implemented');
                return 0;
            },
            removeTask(taskId: TaskId) {
                console.warn('Transaction.removeTask not implemented', taskId);
            },
            captureBreakdown() {
                console.warn('Transaction.captureBreakdown not implemented');
            },
            addLabels(labels: Labels) {
                console.warn('Transaction.addLabels not implemented', labels);
            },
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            startSpan(spanName?: string | null, spanType?: string | null, spanOptions?: SpanOptions): Span | undefined {
                return {
                    name: spanName,
                    type: spanType,
                    sync: false,
                    addLabels(labels: Labels): void {
                        console.warn('Span.addLabels not implemented', labels);
                    },
                    addContext(context: object): void {
                        console.warn('Span.addContext not implemented', context);
                    },
                    end(endTime?: number): void {
                        console.warn('Span.end not implemented', endTime);
                    },
                    duration(): number | null {
                        console.warn('Span.duration not implemented');
                        return 0;
                    },
                } as Span;
            },
            end(endTime?: number) {
                console.warn('Transaction.end not implemented', endTime);
            },
            addTask(taskId: TaskId): TaskId {
                console.warn('Transaction.addTask not implemented', taskId);
                return taskId;
            },
            mark(key: string) {
                console.warn('Transaction.mark not implemented', key);
            },
            isFinished(): boolean {
                console.warn('Transaction.isFinished not implemented');
                return false;
            },
            addContext(context: object) {
                console.warn('Transaction.addContext not implemented', context);
            },
        } as Transaction;

        dispatchSessionEvents([
            createSessionEvent(SessionEventScope.TASK, SessionEventType.TASK_START, transaction.name),
        ]);

        currentTransaction.value = transaction;

        return currentTransaction;
    };

    const endTransaction = (): void => {
        logApmEvent('endTransaction');

        const localTransaction = currentTransaction.value;
        if (!localTransaction) {
            return;
        }

        dispatchSessionEvents([
            createSessionEvent(SessionEventScope.TASK, SessionEventType.TASK_END, localTransaction.name),
        ]);

        currentTransaction.value = undefined;
    };

    const sendEvent = (name: string, type: string, labels?: Labels, options?: SpanOptions): void => {
        logApmEvent('sendEvent', name, type, labels, options);

        const localTransaction = currentTransaction.value;
        if (!localTransaction) {
            return;
        }

        const eventType = getSessionEventTypeFromEventType(type)();
        if (eventType) {
            dispatchSessionEvents([
                createSessionEvent(
                    SessionEventScope.TASK_STEP,
                    eventType,
                    localTransaction.name,
                    defaultStepName,
                    name,
                    labels ?? undefined
                ),
            ]);
        } else {
            console.warn('Impossible to map the event type for the apm event', type);
        }
    };

    const addLabels = (labels: Record<string, LabelValue>): void => {
        logApmEvent('addLabels', labels);

        const localTransaction = currentTransaction.value;
        if (!localTransaction) {
            return;
        }

        const events: SessionEvent[] = [];

        Object.entries(labels).forEach(([key, value]) => {
            const eventType = getSessionEventTypeFromEventType(key)(value.toString());
            if (eventType) {
                events.push(
                    createSessionEvent(
                        SessionEventScope.TASK_STEP,
                        eventType,
                        localTransaction.name,
                        defaultStepName,
                        key
                    )
                );
            } else {
                console.warn('Impossible to map event type for the label', key, value);
            }
        });

        dispatchSessionEvents(events);
    };

    const addMark = (key: string): void => {
        logApmEvent('addMark', key);

        const localTransaction = currentTransaction.value;
        if (!localTransaction) {
            return;
        }

        const eventType = getSessionEventTypeFromEventType(key)();

        if (eventType) {
            dispatchSessionEvents([
                createSessionEvent(SessionEventScope.TASK_STEP, eventType, localTransaction.name, defaultStepName, key),
            ]);
        } else {
            console.warn('Impossible to map the mark event type', key);
        }
    };

    /**
     * Unused since the student is automatically authenticated with the session in the API.
     * @param code
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const setUserContextWithCode = (code: string): void => {
        // console.log('setUserContextWithCode', code);
    };

    const startSpan = (name: string, type: string, labels?: Labels, options?: SpanOptions): void => {
        logApmEvent('startSpan', name, type, labels, options);

        const transaction = currentTransaction.value;

        if (transaction && !currentSpan.value) {
            const span = transaction.startSpan(name, type, options);

            if (span) {
                if (labels) {
                    span.addLabels(labels);
                }
                allSpanNamesUsedInCurrentTransaction.push(name);
                currentSpan.value = span;

                const eventType = getSessionEventTypeFromEventType(type)();

                if (eventType) {
                    dispatchSessionEvents([
                        createSessionEvent(
                            SessionEventScope.TASK_STEP,
                            eventType,
                            transaction.name,
                            defaultStepName,
                            span.name
                        ),
                    ]);
                } else {
                    console.warn('Impossible to map the span type', type);
                }
            }
        } else {
            throw new Error(
                'Impossible to add a Span if there is no active transaction or a span already in progress.'
            );
        }
    };

    const stopSpan = (labels?: Labels): void => {
        logApmEvent('stopSpan', labels);

        const span = currentSpan.value;
        const transaction = currentTransaction.value;

        if (!transaction || !span) {
            return;
        }

        if (labels) {
            span.addLabels(labels);
        }

        currentSpan.value = undefined;
    };

    const startSpanWithId = (name: string, type: string, labels?: Labels, options?: SpanOptions): string => {
        logApmEvent('startSpanWithId', name, type, labels, options);

        const transaction = currentTransaction.value;

        if (transaction) {
            const span = transaction.startSpan(name, type, options);

            if (span) {
                if (labels) {
                    span.addLabels(labels);
                }
                const spanId = uuidv4();
                additionalSpans[spanId] = span;
                allSpanNamesUsedInCurrentTransaction.push(name);
                currentSpan.value = span;

                const eventType = getSessionEventTypeFromEventType(type)();

                if (eventType) {
                    dispatchSessionEvents([
                        createSessionEvent(
                            SessionEventScope.TASK_STEP,
                            eventType,
                            transaction.name,
                            defaultStepName,
                            span.name
                        ),
                    ]);
                } else {
                    console.warn('Impossible to map the span event type', span.type);
                }

                return spanId;
            }
            throw new Error('Impossible to generate a span for the current transaction');
        } else {
            throw new Error(
                'Impossible to add a Span if there is no active transaction or a span already in progress.'
            );
        }
    };

    const stopSpanWithId = (spanId: string, labels?: Labels): void => {
        logApmEvent('stopSpanWithId', spanId, labels);

        const transaction = currentTransaction.value;
        const span = additionalSpans[spanId];

        if (transaction && span) {
            if (labels) {
                span.addLabels(labels);
            }

            delete additionalSpans[spanId];
        }
    };

    const getSpanById = (id: string): Span | undefined => {
        return additionalSpans[id];
    };

    return {
        startTransaction,
        endTransaction,
        sendEvent,
        addLabels,
        addMark,
        setUserContextWithCode,
        startSpan,
        stopSpan,
        startSpanWithId,
        stopSpanWithId,
        getSpanById,
        currentTransaction,
        currentSpan,
    };
}

export type TransactionHandler = (transaction: Transaction) => void;

export function useElasticApmRumOnMountedMetricsAdapter(transactionName: string, transactionType: string) {
    const transactionModule = useElasticApmRumMetricsAdapter();
    const onUnmountedTransactionHandlers: Array<TransactionHandler> = [];
    const onMountedTransactionHandlers: Array<TransactionHandler> = [];

    function addOnUnmountedTransactionHandler(handler: TransactionHandler): void {
        onUnmountedTransactionHandlers.push(handler);
    }

    function addOnMountedTransactionHandler(handler: TransactionHandler): void {
        onMountedTransactionHandlers.push(handler);
    }

    onMounted(() => {
        // never try to start a new transaction if a transaction is already in progress
        if (!currentTransaction.value) {
            const newTransactionRef = transactionModule.startTransaction(transactionName, transactionType);
            const transaction = newTransactionRef.value;
            if (transaction) {
                onMountedTransactionHandlers.forEach((handler) => handler(transaction));
            }
        }
    });

    onBeforeRouteLeave(() => {
        const transaction = currentTransaction.value;
        if (transaction) {
            onUnmountedTransactionHandlers.forEach((handler) => handler(transaction));
            transactionModule.endTransaction();
        }
    });

    return {
        addOnMountedTransactionHandler,
        addOnUnmountedTransactionHandler,
        transactionModule,
    };
}
